/**
 * <size-guide> custom element.
 * Reads its configuration from an embedded <script type="application/json" data-size-guide-config>
 * (rendered server-side by snippets/size-guide.liquid from product metafields + the referenced
 * size_guide_template metaobject) and hydrates the interactive diagram, size switcher, unit
 * toggle, and size-conversion table. No network requests — everything needed is already in the DOM.
 *
 * All display strings (column names, table headers, unit suffixes) come from the config's
 * "translations"/"columnLabels" objects, which Liquid fills in via the `| t` filter from
 * locales/*.json - this file has no hardcoded language so it follows the storefront's locale.
 */

class SizeGuide extends HTMLElement {
  connectedCallback() {
    if (this.__initialized) return;
    this.__initialized = true;

    const configEl = this.querySelector('[data-size-guide-config]');
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('[size-guide] invalid config JSON', error);
      return;
    }

    this.columnLabels = this.config.columnLabels || {};
    this.translations = this.config.translations || {};

    this.state = {
      unit: this.config.defaultInches ? 'in' : 'cm',
      tab: 'conv',
      size: null,
      visibleCols: Object.keys(this.config.columns || {}).filter((key) => this.config.columns[key]),
    };

    const firstAvailable = (this.config.sizes || []).find((s) => s.available) || this.config.sizes?.[0];
    this.state.size = firstAvailable ? firstAvailable.label : null;

    this.renderSizes();
    this.renderUnitToggle();
    this.bindTabs();
    this.bindGear();
    this.updateDiagramValues();
    this.renderTable();
  }

  renderSizes() {
    const host = this.querySelector('[data-size-guide-sizes]');
    if (!host) return;

    host.innerHTML = '';
    (this.config.sizes || []).forEach((size) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'size-guide__size-btn';
      btn.textContent = size.label;
      btn.classList.toggle('is-unavailable', !size.available);
      btn.setAttribute('aria-current', size.label === this.state.size ? 'true' : 'false');

      btn.addEventListener('click', () => {
        this.state.size = size.label;
        this.renderSizes();
        this.updateDiagramValues();
        this.renderTable();
      });

      host.appendChild(btn);
    });
  }

  renderUnitToggle() {
    const host = this.querySelector('[data-size-guide-unit]');
    if (!host) return;

    host.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = input.value === this.state.unit;
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this.state.unit = input.value;
        this.updateDiagramValues();
        this.renderTable();
      });
    });
  }

  updateDiagramValues() {
    const sizeData = (this.config.sizesData || {})[this.state.size] || {};
    const unitSuffix = ' ' + (this.state.unit === 'cm' ? this.translations.unitSuffixCm : this.translations.unitSuffixIn);

    this.querySelectorAll('[data-callout-text]').forEach((group) => {
      const code = group.getAttribute('data-code');
      const valueEl = group.querySelector('[data-value]');
      if (!valueEl) return;

      const entry = sizeData[code];
      if (!entry) {
        valueEl.textContent = '–';
        return;
      }

      const value = this.state.unit === 'cm' ? entry.cm : entry.in;
      valueEl.textContent = value != null ? value + unitSuffix : '–';
    });
  }

  bindTabs() {
    this.querySelectorAll('[data-tab]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.state.tab = btn.getAttribute('data-tab');
        this.querySelectorAll('[data-tab]').forEach((b) => {
          b.setAttribute('aria-current', b === btn ? 'true' : 'false');
        });
        this.renderTable();
      });
    });
  }

  bindGear() {
    const gear = this.querySelector('[data-size-guide-gear]');
    const popover = this.querySelector('[data-size-guide-col-popover]');
    if (!gear || !popover) return;

    Object.keys(this.columnLabels).forEach((key) => {
      if (!(key in (this.config.columns || {}))) return;

      const label = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = this.state.visibleCols.includes(key);
      input.addEventListener('change', () => {
        if (input.checked) {
          if (!this.state.visibleCols.includes(key)) this.state.visibleCols.push(key);
        } else {
          this.state.visibleCols = this.state.visibleCols.filter((c) => c !== key);
        }
        this.renderTable();
      });
      label.appendChild(input);
      label.appendChild(document.createTextNode(' ' + this.columnLabels[key]));
      popover.appendChild(label);
    });

    gear.addEventListener('click', (event) => {
      event.stopPropagation();
      popover.classList.toggle('is-open');
    });

    popover.addEventListener('click', (event) => event.stopPropagation());
    document.addEventListener('click', () => popover.classList.remove('is-open'));
  }

  renderTable() {
    const host = this.querySelector('[data-size-guide-table]');
    if (!host) return;

    const unitSuffix = this.state.unit === 'cm' ? this.translations.unitSuffixCm : this.translations.unitSuffixIn;

    if (this.state.tab === 'body') {
      const sizeData = (this.config.sizesData || {})[this.state.size] || {};
      const rows = Object.entries(sizeData)
        .map(([code, entry]) => {
          const label = (this.config.pointLabels || {})[code] || code;
          const value = this.state.unit === 'cm' ? entry.cm : entry.in;
          return `<tr><td>${escapeHtml(label)}</td><td>${value != null ? value : '–'}</td></tr>`;
        })
        .join('');

      host.innerHTML = `<div class="size-guide__table-scroll"><table class="size-guide__table">
        <thead><tr><th>${escapeHtml(this.translations.tableMeasurementHeader)}</th><th>${escapeHtml(unitSuffix)}</th></tr></thead>
        <tbody>${rows}</tbody>
      </table></div>`;
      return;
    }

    const cols = Object.keys(this.columnLabels).filter((key) => this.state.visibleCols.includes(key));
    const conversion = this.config.conversion || {};
    const sizeLabels = (this.config.sizes || []).map((s) => s.label).filter((label) => conversion[label]);

    const head = cols.map((c) => `<th>${escapeHtml(this.columnLabels[c])}</th>`).join('');
    const body = sizeLabels
      .map((label) => {
        const row = conversion[label] || {};
        const current = label === this.state.size ? ' is-current' : '';
        const cells = cols.map((c) => `<td>${row[c] != null ? row[c] : '–'}</td>`).join('');
        return `<tr class="${current}">${cells}</tr>`;
      })
      .join('');

    host.innerHTML = `<div class="size-guide__table-scroll"><table class="size-guide__table">
      <thead><tr>${head}</tr></thead>
      <tbody>${body}</tbody>
    </table></div>`;
  }
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}
